/**
 * Tokens = typed loader over `design-tokens.json`.
 *
 * SSOT is the JSON. This file ONLY:
 *   - resolves string refs like "palette.ink" into concrete hex values,
 *   - exports a typed `tokens` object for components.
 *
 * Components must never import from 'design-tokens.json' directly nor
 * hardcode colours. Always: `import { tokens } from '../ui/tokens'`.
 */
import raw from './design-tokens.json' with { type: 'json' };

interface RawDoc {
  palette: Record<string, string>;
  surface: Record<string, string>;
  shadow: Record<string, string>;
  radius: Record<string, number>;
  game: Record<string, string>;
  interaction: Record<string, string>;
}

const doc = raw as unknown as RawDoc;

/** Resolve "palette.ink" / raw "#ffffff" / rgba literals. */
function resolve(ref: string): string {
  if (!ref.includes('.') || ref.startsWith('#') || ref.startsWith('rgba') || ref.startsWith('rgb')) {
    return ref;
  }
  const parts = ref.split('.');
  let cur: unknown = doc;
  for (const p of parts) {
    if (cur === null || typeof cur !== 'object') {
      throw new Error(`tokens.resolve: cannot descend into "${p}" of ref "${ref}"`);
    }
    cur = (cur as Record<string, unknown>)[p];
  }
  if (typeof cur !== 'string') {
    throw new Error(`tokens.resolve: ref "${ref}" did not resolve to a string`);
  }
  return cur;
}

export const tokens = {
  color: {
    white:             doc.palette['white']!,
    nearWhite:         doc.palette['nearWhite']!,
    ink:               doc.palette['ink']!,
    pageBackground:    resolve(doc.surface['page']!),
    heroH1:            resolve(doc.surface['heroH1Text']!),
    heroText:          resolve(doc.surface['heroBodyText']!),
    sloganBackground:  resolve(doc.surface['sloganBackground']!),
    sloganText:        resolve(doc.surface['sloganText']!),
    qrDark:            resolve(doc.surface['qrDark']!),
    qrLight:           resolve(doc.surface['qrLight']!),
    selectedBorder:    resolve(doc.interaction['selectedBorder']!),
  },
  shadow: {
    hero: doc.shadow['hero']!,
  },
  radius: {
    hero: doc.radius['hero']!,
    card: doc.radius['card']!,
    pill: doc.radius['pill']!,
  },
  game: {
    stickmanStroke: resolve(doc.game['stickmanStroke']!),
  },
} as const;

// Re-exported for the contract test only.
export const __raw = doc;
